<template>
	<div class="survey-forms">
		<h2 class="content-block">
			<img src="@/assets/colpuno_big_transparent.png" class="header-image" v-if="showHeaderImage" />
			<div class="header-title">General Survey</div>
		</h2>

		<DxPopup v-model:visible="submitError" @hidden="submitError = false" title="Error submitting survey"
			:shading="true" shadingColor="#ccca" :showTitle="true" :showCloseButton="true" :dragEnabled="false" :hideOnOutsideClick="true"
			max-width="400px" max-height="280px">
			<div class="submitsurvey-error-body">{{ submitErrorMsg }}</div>
		</DxPopup>

		<DxPopup v-model:visible="videoPopup" :shading="true" shadingColor="#ccca" :showTitle="true" :showCloseButton="true" :dragEnabled="false" :hideOnOutsideClick="true" :wrapper-attr="{ class: 'video-popup' }"
			max-width="480px" max-height="520px" :fullScreen="smallScreenVideo"
			:onShown="videoPopupToggled" :onHiding="videoPopupToggled">
			<video controls src="../assets/general_survey_vid.mp4" ref="videoPopupVideo" width="100%" height="100%"/>
		</DxPopup>
		
		<div class="content-block dx-card responsive-paddings application-form" v-if="existingForm.id">
			<div class="existing-warning">
				<i class="dx-icon dx-icon-check" style="font-size: 48px;color:green;"></i><br>
				<b>Thank you for submitting your survey form.</b><br>
				If necessary, we will send you an email with any further details.
			</div>
		</div>

		<div class="content-block dx-card responsive-paddings application-form" v-if="!existingForm.id">
			<form @submit.prevent="" enctype="multipart/form-data">
				<DxForm id="general-survey-dxform" ref="formRef" label-mode="floating" :col-count="1" :form-data="applicationData" validation-group="gsurveyValidationGroup">
					<DxTabbedItem>
						<DxTabPanelOptions :swipe-enabled="false" :repaint-changes-only="true" :defer-rendering="false" :selected-index="tabSelectedIndex" @titleClick="formTabChanged" />
						
						<DxTab title="Introduction">
							<DxSimpleItem>
								<DxBox :direction="smallScreenVideo ? 'col' : 'row'" width="100%" class="simple-item-descriptive dx-card dx-theme-accent-as-border-color">
									<DxItem :ratio="smallScreenVideo ? 0 : 1" :baseSize="smallScreenVideo ? 'auto' : 0">
										<p class="simple-item-descriptive-title">Welcome to the Colpuno Survey</p>
										<p>Thank you for visiting! Your insights are essential in shaping tools and opportunities that empower Filipino nurses worldwide.</p>
										<p>By taking a few minutes to complete this survey, you'll help us build a platform that supports your career goals and strengthens our nursing community.</p>
										<p>Let's make a difference together — <i>your voice matters!</i></p>
									</DxItem>
									<DxItem :visible="!smallScreenVideo" :ratio="0" :baseSize="10"></DxItem>
									<DxItem :ratio="smallScreenVideo ? 0 : 0" :baseSize="smallScreenVideo ? 300 : 200">
										<div class="simple-item-videobox" @click="videoPopup = true">
											<div><i style="font-size:48px;" class="dx-icon-video"></i></div>
											<span>Play Video</span>
										</div>
									</DxItem>
								</DxBox>
							</DxSimpleItem>
							<DxButtonItem horizontal-alignment="right">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(1)" />
							</DxButtonItem>
						</DxTab>

						<DxTab title="General Information">
							<DxGroupItem caption="Personal details" :col-count="3">
								<DxSimpleItem data-field="first_name" :is-required="true" :label="{ text: 'First/Given name' }" />
								<DxSimpleItem data-field="middle_name" :is-required="false" />
								<DxSimpleItem data-field="last_name" :is-required="true" :label="{ text: 'Last/Family name' }" />
								<DxSimpleItem data-field="email" :is-required="true">
									<DxRequiredRule message="Email is required"/><DxEmailRule message="Email is invalid"/>
								</DxSimpleItem>
								<DxSimpleItem data-field="birthday" editor-type="dxDateBox" :editor-options="{ type: 'date', pickerType: 'native', max: Date(), dateSerializationFormat: 'yyyy-MM-dd' }" :is-required="true" />
								<DxSimpleItem data-field="gender" editor-type="dxSelectBox" :editor-options="genderListOptions" :is-required="true" />
								<DxSimpleItem data-field="country" editor-type="dxSelectBox" :editor-options="countryListOptions" :is-required="true" />
								<DxSimpleItem data-field="region" editor-type="dxSelectBox" :editor-options="regionListOptions" :is-required="true" :visible="showRegionList" />
								<DxSimpleItem data-field="survey_from" editor-type="dxSelectBox" :editor-options="surveyFromOptions" :is-required="true" />
							</DxGroupItem>
							<DxGroupItem caption="Educational background" :col-count="1">
								<DxSimpleItem data-field="nursing_school" editor-type="dxSelectBox" :editor-options="schoolListOptions" :label="{ text: 'Nursing School Attended' }" :is-required="true" :visible="!customSchoolName"/>
								<DxSimpleItem :col-span="3" css-class="group-hint-header" :visible="!customSchoolName">
									<i class="dx-icon dx-icon-info"></i>
									<span>Click/tap on the boxes and type to search for your school, or <a href="#" @click.prevent="customSchoolName = true">click here</a> to type your own school.</span>
								</DxSimpleItem>
								<DxSimpleItem data-field="nursing_school" :label="{ text: 'Nursing School Attended (type your own)' }" :is-required="true" :visible="customSchoolName"/>
								<DxSimpleItem data-field="pnle_years" :is-required="true" :label="{ text: 'PNLE Completion Year' }" editor-type="dxNumberBox" :editor-options="{ min: 1900, max: (new Date()).getFullYear() + 1, showSpinButtons: true }" />
								<DxSimpleItem>
									<LanguageProficency :initial="applicationData.languages" validationGroup="gsurveyValidationGroup" @valueChanged="applicationData.languages = $event" />
								</DxSimpleItem>
							</DxGroupItem>
							<DxButtonItem horizontal-alignment="right" :visible="showFinalizeButton">
								<DxButtonOptions icon="chevrondoubleright" text="Back to Conclusion" type="success" :onClick="() => doFormTabChanged(6)" />
							</DxButtonItem>
							<DxButtonItem horizontal-alignment="right" :visible="!showFinalizeButton">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(2)" />
							</DxButtonItem>
						</DxTab>

						<DxTab title="Employment Background">
							<DxGroupItem caption="Current employment status" :col-count="1">
								<DxSimpleItem data-field="current_job_status" editor-type="dxSelectBox" :editor-options="currentJobStatusListOptions" :is-required="true" />
							</DxGroupItem>
							
							<DxGroupItem caption="Employment Details" :col-count="3" :visible="applicationData.current_job_status == 'employed'">
								<DxSimpleItem data-field="employer_type" editor-type="dxSelectBox" :editor-options="employerTypeListOptions" :is-required="true" />
								<DxSimpleItem data-field="employer_type_other" :label="{ text: 'Please specify non-healthcare sector' }" :is-required="true" :col-span="2" :visible="applicationData.employer_type == 'non-healthcare'" />
								<DxEmptyItem :visible="applicationData.employer_type != 'non-healthcare'" :col-span="2" />
								
								<DxSimpleItem data-field="patient_contact" editor-type="dxSelectBox" :editor-options="patientContactListOptions" :is-required="true" />
								<DxSimpleItem data-field="job_position" editor-type="dxSelectBox" :editor-options="jobPositionListOptions" :is-required="true" :col-span="2" :visible="applicationData.patient_contact == 'contact'" />
								<DxSimpleItem data-field="job_position" :label="{ text: 'Job Description' }" :is-required="true" :col-span="2" :visible="applicationData.patient_contact == 'no-contact'" />
								<DxEmptyItem :visible="!applicationData.patient_contact" :col-span="2" />
								
								<DxSimpleItem data-field="employer_site" :label="{ text: 'Employer Location' }" editor-type="dxSelectBox" :editor-options="employerSiteOptions" :is-required="true" />
								<DxSimpleItem :visible="applicationData.employer_site == 'national'" :is-required="applicationData.employer_site == 'national'"
									data-field="employer_region" editor-type="dxSelectBox" :editor-options="employerRegionOptions" :col-span="2"  />
								<DxSimpleItem :visible="applicationData.employer_site == 'international'" :is-required="applicationData.employer_site == 'international'"
									data-field="employer_country" editor-type="dxSelectBox" :editor-options="employerCountryOptions" :col-span="2" />
								<DxEmptyItem :visible="!applicationData.employer_site" :col-span="2" />

								<DxSimpleItem data-field="career_nextstep" :label="{ text: 'What are your plans for the next step in your career path?' }" editor-type="dxSelectBox" :editor-options="careerNextstepOptions" :is-required="true" :col-span="3" />
							</DxGroupItem>

							<DxGroupItem :col-count="3" :visible="applicationData.current_job_status == 'unemployed-seeking'">
								<DxSimpleItem data-field="employment_before" :label="{ visible: false }" editor-type="dxCheckBox" :editor-options="employmentBeforeOptions" :col-span="3" />
								<DxGroupItem :visible="applicationData.employment_before" :col-span="3" :col-count="3">
									<DxSimpleItem data-field="patient_contact" editor-type="dxSelectBox" :editor-options="patientContactListOptions" :is-required="true" />
									<DxSimpleItem data-field="job_position" editor-type="dxSelectBox" :editor-options="jobPositionListOptions" :is-required="true" :col-span="2" :visible="applicationData.patient_contact == 'contact'" />
									<DxSimpleItem data-field="job_position" :label="{ text: 'Job Description' }" :is-required="true" :col-span="2" :visible="applicationData.patient_contact == 'no-contact'" />
									<DxEmptyItem :visible="!applicationData.patient_contact" :col-span="2" />
									<DxSimpleItem data-field="employer_site" :label="{ text: 'Employer Location' }" editor-type="dxSelectBox" :editor-options="employerSiteOptions" :is-required="true" />
									<DxSimpleItem :visible="applicationData.employer_site == 'national'" :is-required="applicationData.employer_site == 'national'"
										data-field="employer_region" editor-type="dxSelectBox" :editor-options="employerRegionOptions" :col-span="2"  />
									<DxSimpleItem :visible="applicationData.employer_site == 'international'" :is-required="applicationData.employer_site == 'international'"
										data-field="employer_country" editor-type="dxSelectBox" :editor-options="employerCountryOptions" :col-span="2" />
									<DxEmptyItem :visible="!applicationData.employer_site" :col-span="2" />
								</DxGroupItem>
								<DxGroupItem caption="Roughly how long ago was this employment?" :col-count="3" :visible="applicationData.employment_before" :col-span="3">
									<DxSimpleItem data-field="employment_last_years" :label="{ text: 'Years' }" editor-type="dxNumberBox" :editor-options="{ min: 0, max: 100 }" />
									<DxSimpleItem data-field="employment_last_months" :label="{ text: 'Months' }" editor-type="dxNumberBox" :editor-options="{ min: 0, max: 12 }" />
								</DxGroupItem>
								<DxGroupItem caption="Total Duration of Employment" :col-count="3" :visible="applicationData.employment_before" :col-span="3">
									<DxSimpleItem data-field="job_duration_years" :is-required="true" :label="{ text: 'Years' }" editor-type="dxNumberBox" :editor-options="{ min: 0, max: 100 }" />
									<DxSimpleItem data-field="job_duration_months" :is-required="true" :label="{ text: 'Months' }" editor-type="dxNumberBox" :editor-options="{ min: 0, max: 12 }" />
								</DxGroupItem>
							</DxGroupItem>
							
							<DxGroupItem caption="Preferred Job" :col-count="3" :visible="(applicationData.current_job_status == 'employed' && applicationData.career_nextstep != 'happy') || applicationData.current_job_status == 'unemployed-seeking'" >
								<DxSimpleItem data-field="prefer_position" :label="{ text: 'Preferred Job Position' }" editor-type="dxSelectBox" :editor-options="preferPositionListOptions" :is-required="true" :col-span="3" />
								<DxSimpleItem data-field="prefer_site" :label="{ text: 'Preferred Job Location' }" editor-type="dxSelectBox" :editor-options="employerSiteOptions" :is-required="true" :col-span="1" />
								<DxSimpleItem data-field="prefer_region" :label="{ text: 'Preferred Job Region' }" editor-type="dxSelectBox" :editor-options="employerRegionOptions" :is-required="true" :col-span="2" :visible="applicationData.prefer_site == 'national'" />
								<DxSimpleItem data-field="prefer_country" :label="{ text: 'Preferred Job Country' }" editor-type="dxSelectBox" :editor-options="employerCountryOptions" :is-required="true" :col-span="2" :visible="applicationData.prefer_site == 'international'" />
							</DxGroupItem>
							<DxGroupItem caption="Duration of current employment" :col-count="3" :visible="applicationData.current_job_status == 'employed'">
								<DxSimpleItem data-field="job_duration_years" :is-required="true" :label="{ text: 'Years' }" editor-type="dxNumberBox" :editor-options="{ min: 0, max: 100 }" />
								<DxSimpleItem data-field="job_duration_months" :is-required="true" :label="{ text: 'Months' }" editor-type="dxNumberBox" :editor-options="{ min: 0, max: 12 }" />
							</DxGroupItem>

							<DxButtonItem horizontal-alignment="right" :visible="showFinalizeButton">
								<DxButtonOptions icon="chevrondoubleright" text="Back to Conclusion" type="success" :onClick="() => doFormTabChanged(6)" />
							</DxButtonItem>
							<DxButtonItem horizontal-alignment="right" :visible="!showFinalizeButton">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(3)" />
							</DxButtonItem>
						</DxTab>

						<DxTab title="Professional Skills and Development">
							<DxGroupItem>
								<DxSimpleItem>
									<RatedTopics label="Rate your proficiency in the following" validationGroup="gsurveyValidationGroup" :topics="proficiencyTopics" :ratings="proficiencyRatings" :initial="applicationData.rated_proficiency" @valueChanged="applicationData.rated_proficiency = $event;" />
								</DxSimpleItem>
								<DxSimpleItem data-field="future_skills" :label="{ text: 'Are there specific skills you\'d like to improve or acquire?' }" editor-type="dxTextArea" :editor-options="{ height: 100 }" />
								<DxSimpleItem data-field="interest_further_edu" :label="{ text: 'Are you interested in Further Education or Training?' }" editor-type="dxSelectBox" :editor-options="interestFurtherEduOptions" :is-required="true" />
								<DxSimpleItem data-field="interest_further_edu_details" :label="{ text: 'What kind of education/training?' }" :visible="applicationData.interest_further_edu == 'yes'" :is-required="true" />
							</DxGroupItem>
							<DxButtonItem horizontal-alignment="right" :visible="showFinalizeButton">
								<DxButtonOptions icon="chevrondoubleright" text="Back to Conclusion" type="success" :onClick="() => doFormTabChanged(6)" />
							</DxButtonItem>
							<DxButtonItem horizontal-alignment="right" :visible="!showFinalizeButton">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(4)" />
							</DxButtonItem>
						</DxTab>

						<DxTab title="Career Goals and Aspirations">
							<DxGroupItem caption="What are your professional nursing goals for the next 5 years?">
								<DxSimpleItem data-field="career_goals" :label="{ text: 'Select one or more goals:' }" editor-type="dxTagBox" :editor-options="careerGoalsOptions" :is-required="true" />
								<DxSimpleItem data-field="career_goals_other" :label="{ text: 'Please specify other goals:' }" :visible="applicationData.career_goals.includes('other')" :is-required="true" />
							</DxGroupItem>
							<DxGroupItem caption="What motivates you most in your nursing career?">
								<DxSimpleItem data-field="career_motivation" :label="{ text: 'Select one or more motivations:' }" editor-type="dxTagBox" :editor-options="careerMotivationOptions" :is-required="true" />
								<DxSimpleItem data-field="career_motivation_other" :label="{ text: 'Please specify other motivation:' }" :visible="applicationData.career_motivation.includes('other')" :is-required="true" />
							</DxGroupItem>
							<DxGroupItem caption="How confident are you in defining your career path?">
								<DxSimpleItem data-field="career_confidence" editor-type="dxSelectBox" :editor-options="careerConfidenceOptions" :is-required="true" />
							</DxGroupItem>
							<DxButtonItem horizontal-alignment="right" :visible="showFinalizeButton">
								<DxButtonOptions icon="chevrondoubleright" text="Back to Conclusion" type="success" :onClick="() => doFormTabChanged(6)" />
							</DxButtonItem>
							<DxButtonItem horizontal-alignment="right" :visible="!showFinalizeButton">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(5)" />
							</DxButtonItem>
						</DxTab>
						
						<DxTab title="Assessment and Portfolio">
							<DxSimpleItem data-field="personality_assess" :label="{ text: 'Would you be interested in a tool to learn more about your personality to better promote strengths for employment applications?' }" editor-type="dxSelectBox" :editor-options="personalityAssessOptions" :is-required="true" />
							<DxSimpleItem data-field="country_assess" :label="{ text: 'Would you be interested in a tool to help you understand what country would be best for your employment preferences?' }" editor-type="dxSelectBox" :editor-options="countryAssessOptions" :is-required="true" />
							<DxSimpleItem data-field="personal_cv" :label="{ text: 'Would you be interested in a tool to create a personalized CV?' }" editor-type="dxSelectBox" :editor-options="personalCvOptions" :is-required="true" />
							<DxSimpleItem data-field="personal_portfolio" :label="{ text: 'Would you be interested in a tool to build a digital portfolio showcasing your skills and experience?' }" editor-type="dxSelectBox" :editor-options="personalPortfolioOptions" :is-required="true" />
							<DxSimpleItem data-field="personal_planning" :label="{ text: 'Would you be interested in a tool to access career planning resources?' }" editor-type="dxSelectBox" :editor-options="personalPlanningOptions" :is-required="true" /> 
							<DxSimpleItem data-field="other_tools" :label="{ text: 'Are there any other tools that would be of interest to you?' }" editor-type="dxTextArea" :editor-options="{ height: 100 }" />
							<DxButtonItem horizontal-alignment="right" :visible="showFinalizeButton">
								<DxButtonOptions icon="chevrondoubleright" text="Back to Conclusion" type="success" :onClick="() => doFormTabChanged(6)" />
							</DxButtonItem>
							<DxButtonItem horizontal-alignment="right" :visible="!showFinalizeButton">
								<DxButtonOptions text="Next" type="default" :onClick="() => doFormTabChanged(6)" />
							</DxButtonItem>
						</DxTab>

						<DxTab title="Conclusion">
							<DxSimpleItem>
								<div class="simple-item-descriptive dx-card dx-theme-accent-as-border-color">
									<p class="simple-item-descriptive-title">Thank You!</p>
									<p>Thank you for completing the Colpuno Nursing Empowerment Survey.</p>
									<p style="font-style: italic; text-align: left;">Your responses are completely anonymous!</p>
									<p>As a participant, you now have the opportunity to join the beta phase of Colpuno — a platform designed to help you:</p>
									<ul :class="{ 'list-tiny': isTiny }">
										<li>Build your professional profile and CV.</li>
										<li>Showcase your skills through a digital portfolio.</li>
										<li>Access tools for career planning and personal growth.</li>
									</ul>
									<p>Your insights help us empower the Filipino nursing community and create meaningful opportunities for growth.</p>
									<p class="simple-item-descriptive-title">Would you like to sign up for the beta phase of Colpuno?</p>
									<DxBox direction="row">
										<DxItem :ratio="0" :baseSize="40">
											<DxCheckBox v-model:value="applicationData.agree_beta" />
										</DxItem>
										<DxItem :ratio="1">
											Yes, I would like to participate in the COLPUNO Beta Community.
										</DxItem>
									</DxBox>
									<p style="font-style: italic; padding-left: 40px; margin-top: 5px;">If you'd prefer not to participate, please unselect this option.</p>
									<DxBox direction="row">
										<DxItem :ratio="0" :baseSize="40">
											<DxCheckBox v-model:value="applicationData.agree_privacy_policy" />
										</DxItem>
										<DxItem :ratio="1">
											<div><div>I have read and agree with the <a href="/privacy.html" target="_blank">Privacy Policy</a> regarding usage of my data.</div></div>
										</DxItem>
									</DxBox>
									<p>Thank you for your valuable contribution!</p>
								</div>
							</DxSimpleItem>
							<DxButtonItem :button-options="{ type: 'success', text: 'Submit Form', useSubmitBehavior: false, onClick: submitForm }" />
						</DxTab>
					</DxTabbedItem>
				</DxForm>
			</form>
			<DxLoadPanel :position="{ of: '.application-form' }" :shading="true" shadingColor="rgba(0,0,0,0.4)" v-model:visible="loadingVisible" />
		</div>
	</div>
</template>

<script setup>
/* eslint-disable */
import { DxForm, DxSimpleItem , DxGroupItem, DxButtonItem, DxButtonOptions, DxEmptyItem, DxRequiredRule, DxEmailRule, DxTabbedItem, DxTab, DxTabPanelOptions, DxLabel } from "devextreme-vue/form";
import DxTextArea from 'devextreme-vue/text-area';			// eslint-disable-line no-unused-vars
import DxSelectBox from 'devextreme-vue/select-box';		// eslint-disable-line no-unused-vars
import { DxDateBox } from 'devextreme-vue/date-box';		// eslint-disable-line no-unused-vars
import DxTagBox from 'devextreme-vue/tag-box';				// eslint-disable-line no-unused-vars
import DxCheckBox from 'devextreme-vue/check-box';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxBox, DxItem } from 'devextreme-vue/box';
import DxPopup from "devextreme-vue/popup"
import DxButton from 'devextreme-vue/button';
import { inject, ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useRouter, useRoute } from 'vue-router';
import LanguageProficency from '../components/language-proficency.vue';
import RatedTopics from '../components/rated-topics.vue';
import to from 'await-to-js';
import DataSource from 'devextreme/data/data_source';
import notify from 'devextreme/ui/notify';
import { sizes as mediaSizes, subscribe as subscribeMediaQuery, unsubscribe as unsubscribeMediaQuery } from '../utils/media-query';
import countries from '../data/countries.js';
import regions from '../data/regions.js';
import schools from '../data/schools.js';
import specialities from '../data/specialities.js';
import appInfo from '../app-info.js';

const $http = inject('$http');
const isTiny = ref(mediaSizes()['screen-x-small']);
const smallScreenVideo = ref(mediaSizes()['screen-x-small'] || mediaSizes()['screen-small']);
const router = useRouter();
const route = useRoute();
const SURVEY_TYPE = 'general';
const SURVEY_STOREKEY = appInfo.localPrefix + 'Survey_' + SURVEY_TYPE;

const mediaQueryHandler = () => {
	isTiny.value = mediaSizes()['screen-x-small'];
	smallScreenVideo.value =  mediaSizes()['screen-x-small'] || mediaSizes()['screen-small'];
};
onMounted(() => {
	subscribeMediaQuery(mediaQueryHandler);
	mediaQueryHandler();
	// Automatically set source of survey if encoded in url parameters 's' (from QR codes, redirects, etc.)
	if (route.query && route.query.s) {
		// Check this is a valid survey source first
		let validSource = surveyFromOptions.dataSource[0].items.find(item => item.id == route.query.s);	
		if (validSource) {
			applicationData.value.survey_from = validSource.id;
		}
	}
});
onUnmounted(() => {
	unsubscribeMediaQuery(mediaQueryHandler);
});
const videoPopupToggled = () => {
	if (videoPopup.value) {		// Video was just opened
		videoPopupVideo.value.play();
	} else {					// Video was just closed
		videoPopupVideo.value.pause();
	}
};

const applicationData = ref({
	languages: [],
	rated_proficiency: [],
	career_goals: [],
	career_motivation: [],
	portfolio_interest: [],
	employment_before: false,
	agree_privacy_policy: false,
	agree_beta: true,
});
const loadingVisible = ref(false);
const submitError = ref(false);
const submitErrorMsg = ref('');
const tabSelectedIndex = ref(0);
const formRef = ref(null);
const showHeaderImage = ref(true);
const videoPopup = ref(false);
const videoPopupVideo = ref(null);
const showFinalizeButton = ref(false);

const formTabChanged = (event) => {
	doFormTabChanged(event.itemIndex);
};
const doFormTabChanged = (tabIndex) => {
	tabSelectedIndex.value = tabIndex;
	showHeaderImage.value = tabIndex == 0;
}

const existingForm = ref({ id: null, edit_secret: null });
const existingFormStore = window.localStorage.getItem(SURVEY_STOREKEY);
if (existingFormStore) {
	// Load existing ID and secret from local storage if possible.
	existingForm.value.id = existingFormStore.split(' ')[0];
	existingForm.value.edit_secret = existingFormStore.split(' ')[1];
}

const submitForm = async () => {
	// Manually switch to the tab with the first invalid field, since we don't highlight them in any other way.
	let validationResult = formRef.value.instance.validate();
	console.log(validationResult);
	if (!validationResult.isValid) {
		showFinalizeButton.value = true;
		let switchToTab = null;
		validationResult.brokenRules.forEach(rule => {
			let ruleElement = rule.validator.element();
			let tabElement = ruleElement?.closest('.dx-item[role=tabpanel]') ?? null;
			let tabIndex = tabElement ? [...tabElement.parentElement.children].indexOf(tabElement) : null;
			if (tabIndex !== null && switchToTab == null) {
				switchToTab = tabIndex;
			}
		});
		if (switchToTab != null	) {
			// We must switch to the tab first, then validate, otherwise the validation elements are not visible!	
			doFormTabChanged(switchToTab);
			nextTick(() => {
				formRef.value.instance.validate();
			});
		}
		return;
	}
	
	if (!applicationData.value.agree_privacy_policy) {
		notify({ message: "Sorry, you must agree to the privacy policy before submitting this form!", position: 'center', shading: true, shadingColor: '#cccc' }, "error", 3000);
		return;
	}
	
	// Preprocess form data to remove any labels since we won't need to store them.
	// We clone the app data in preperation for preprocessing since in the event of an error, we want our original UI/forms to still work.
	let applicationDataClone = JSON.parse(JSON.stringify(applicationData.value));
	for (var i in applicationDataClone) {
		if (!Array.isArray(applicationDataClone[i]))
			continue;
		for (var j in applicationDataClone[i]) {
			delete applicationDataClone[i][j].label;
		}
	}
	
	console.log("Posting data", applicationDataClone);
	loadingVisible.value = true;
	let returnVal, err;
	let postUrl = '/survey';
	let formSubmission = {
		'_method': 'POST',
		'type': SURVEY_TYPE,
		'results': JSON.stringify(applicationDataClone),
	};
	[ err, returnVal ] = await to($http.post(postUrl, formSubmission));
	loadingVisible.value = false;
	if (err) {
		console.error([ err ]);
		let errorMsg = err.message ? err.message : 'Unknown error';
		if (err.response && err.response.statusText) {
			errorMsg += "\n" + err.response.statusText;
		}
		submitError.value = true;
		submitErrorMsg.value = "Error submitting your form:\n" + errorMsg;
		return;
	}
	console.log(returnVal);

	// When complete, we store the existing form ID and secret in local storage as an easy way to mark that the form has been completed (since this is unauthenticated).
	// Secrets are split by space since these are never generated.
	window.localStorage.setItem(SURVEY_STOREKEY, returnVal.data.id + ' ' + returnVal.data.edit_secret);
	notify({ message: "Your survey has been submitted successfully! Thank you for your time.", position: 'center', shading: true, shadingColor: '#cccc' }, "success", 3000);
	setTimeout(() => {
		router.go(0);
	}, 3000);
}

// General Information Tab
const genderListOptions = { dataSource: [ 'Male', 'Female', 'Diverse' ] };
const countriesData = new DataSource({ store: countries, pageSize: 10, paginate: true, sort: (v) => v.code == 'PH' ? '!' : v.name.toLowerCase() });
const countryListOptions = { dataSource: countriesData, valueExpr: 'code', displayExpr: 'name', searchEnabled: true };
const regionListOptions = { dataSource: regions.filter(r => r.code.startsWith('PH-')), valueExpr: 'code', displayExpr: 'name', searchEnabled: true };
const showRegionList = computed(() => applicationData.value.country == 'PH');
const schoolsData = new DataSource({ store: schools, sort: 'title', pageSize: 10, paginate: true });
const schoolListOptions = { dataSource: schoolsData, valueExpr: 'slug', displayExpr: 'title', searchEnabled: true,  };
const customSchoolName = ref(false);
const surveyFromOptions = { grouped: true, dataSource: [
	{ key: "Where did you find this survey from?", items: [
		{ id: 'niner', name: "9.0 Niner Event" },
		{ id: 'tnc', name: "Transcultural Nursing Conference" },
		{ id: 'webpage', name: "Colupno Webpage" },
		{ id: 'websearch', name: "Online Search" },
		{ id: 'webad', name: "Online Advertisement" },
		{ id: 'person', name: "Friend or Colleague" },
		{ id: 'other', name: "Other" },
	]},
], valueExpr: 'id', displayExpr: 'name' };

// Employment Background Tab
const jobStatuses = [
	{ id: "employed", name: "Employed" },
	{ id: "unemployed-seeking", name: "Unemployed but actively seeking opportunities" },
	{ id: "unemployed-not-seeking", name: "Unemployed and not seeking opportunities" },
];
const currentJobStatusListOptions = { dataSource: jobStatuses, valueExpr: 'id', displayExpr: 'name' };

// Employment Background Tab: Employed
const employerTypes = [
	{ id: 'hospital', name: 'Hospital' },
	{ id: 'community', name: 'Community Health Center' },
	{ id: 'nursing-home', name: 'Nursing Home' },
	{ id: 'school-company', name: 'School/Company' },
	{ id: 'non-healthcare', name: 'Non-Healthcare Sector' },
];
const employerTypeListOptions = { dataSource: employerTypes, valueExpr: 'id', displayExpr: 'name' };
const patientContacts = [
	{ id: 'contact', name: 'Direct contact with patients' },	
	{ id: 'no-contact', name: 'No direct contact with patients' },	
];
const patientContactListOptions = { dataSource: patientContacts, valueExpr: 'id', displayExpr: 'name' };
const jobPositionListOptions = { dataSource: JSON.parse(JSON.stringify(specialities)), displayExpr: 'name', valueExpr: 'name', grouped: true,
	placeholder: "Select or type a custom item...",	
	acceptCustomValue: true, 
	onCustomItemCreating: (args) => {		// NB: Usage of custom means our datasource must be a clone of the original!
		// Create a new item under the 'other' group.
		if (!args.text) {
			args.customItem = null;
			return;
		}
		const { component, text } = args;
		const currentItems = component.option('items');
		const otherGroup = currentItems.find(cu => cu.key == 'Other');
		if (!otherGroup || !otherGroup.items[0]) {
			args.customItem = null;
			return;
		}
		otherGroup.items[0].name = text.trim();
		args.customItem = otherGroup.items[0];
	},
};
const employerSiteOptions = { dataSource: [ { id: 'national', name: "National (Philipines)" }, { id: 'international', name: "International" } ], valueExpr: 'id', displayExpr: 'name' };
const employerRegionOptions = { dataSource: regions.filter(r => r.code.startsWith('PH-')), valueExpr: 'code', displayExpr: 'name', searchEnabled: true };
const employerCountryOptions = { dataSource: countries.filter(c => c.code != 'PH'), valueExpr: 'code', displayExpr: 'name', searchEnabled: true };
const careerNextstepOptions = { dataSource: [
	{ id: 'happy', name: "Happy with my position right now" },
	{ id: 'promote', name: "Looking to move up within my current organization" },
	{ id: 'position', name: "Looking for a new position elsewhere" },
	{ id: 'field', name: "Looking for a new field" },
], valueExpr: 'id', displayExpr: 'name' };
const preferPositionListOptions = { dataSource: JSON.parse(JSON.stringify(specialities)), displayExpr: 'name', valueExpr: 'name', grouped: true,
	placeholder: "Select or type a custom item...",	
	acceptCustomValue: true, 
	onCustomItemCreating: (args) => {		// NB: Usage of custom means our datasource must be a clone of the original!
		// Create a new item under the 'other' group.
		if (!args.text) {
			args.customItem = null;
			return;
		}
		const { component, text } = args;
		const currentItems = component.option('items');
		const otherGroup = currentItems.find(cu => cu.key == 'Other');
		if (!otherGroup || !otherGroup.items[0]) {
			args.customItem = null;
			return;
		}
		otherGroup.items[0].name = text.trim();
		args.customItem = otherGroup.items[0];
	},
};

// Employment Background Tab: Seeking Opportunities
const employmentBeforeOptions = { text: "I have been employed in the Healthcare sector before.", iconSize: 24 };

// Professional Skills and Development Tab
const proficiencyTopics = [
	{ code: 'ehr', name: "Electronic Health Records (EHR)" },
	{ code: 'wound', name: "Wound Care" },
	{ code: 'infect', name: "Infection Control" },
	{ code: 'pain', name: "Pain Management" },
	{ code: 'other', name: "Other Specialized Skills" },
];
const proficiencyRatings = [
	{ id: '5', name: 'Expert' },
	{ id: '4', name: 'Advanced' },
	{ id: '3', name: 'Intermediate' },
	{ id: '2', name: 'Beginner' },
	{ id: '1', name: 'No Experience' }
];
const interestFurtherEduOptions = { dataSource: [ { id: 'yes', name: "Yes" }, { id: 'no', name: "No" } ], valueExpr: 'id', displayExpr: 'name' };

// Career Goals and Aspirations Tab
const careerGoalsOptions = { dataSource: [
		{ id: 'specialty', name: "Gain a specialty certification" },
		{ id: 'leadership', name: "Move into a leadership position" },
		{ id: 'research', name: "Engage in research" },
		{ id: 'international', name: "Work internationally" },
		{ id: 'other', name: "Other" },
	], displayExpr: 'name', valueExpr: 'id',
};
const careerMotivationOptions = { dataSource: [
		{ id: 'care', name: "Patient care and satisfaction" },
		{ id: 'growth', name: "Professional growth and development" },
		{ id: 'financial', name: "Financial stability" },
		{ id: 'difference', name: "Making a difference in the healthcare field" },
		{ id: 'other', name: "Other" },
	], displayExpr: 'name', valueExpr: 'id',
};
const careerConfidenceOptions = { dataSource: [
	{ id: 'confident', name: "Very confident" },
	{ id: 'somewhat', name: "Somewhat confident" },
	{ id: 'unsure', name: "Unsure and need guidance" },
], valueExpr: 'id', displayExpr: 'name' };

// Assessment and Portfolio Tab
const personalityAssessOptions = { dataSource: [
	{ key: "Would you be interested in a tool to learn more about your personality to better promote strengths for employment applications?", items: [
		{ id: 'yes', name: "Yes" },
		{ id: 'no', name: "No" },
	]},
], valueExpr: 'id', displayExpr: 'name', grouped: true };
const countryAssessOptions = { dataSource: [
	{ key: "Would you be interested in a tool to help you understand what country would be best for your employment preferences?", items: [
		{ id: 'yes', name: "Yes" },
		{ id: 'no', name: "No" },
	]},
], valueExpr: 'id', displayExpr: 'name', grouped: true };
const personalCvOptions = { dataSource: [ { key: "Would you be interested in a tool to create a personalized CV?", items: [
		{ id: 'yes', name: "Yes" },
		{ id: 'no', name: "No" },
	]},
], valueExpr: 'id', displayExpr: 'name', grouped: true };
const personalPortfolioOptions = { dataSource: [ { key: "Would you be interested in a tool to build a digital portfolio showcasing your skills and experience?", items: [
		{ id: 'yes', name: "Yes" },
		{ id: 'no', name: "No" },
	]},
], valueExpr: 'id', displayExpr: 'name', grouped: true };
const personalPlanningOptions = { dataSource: [ { key: "Would you be interested in a tool to access career planning resources?", items: [
	{ id: 'yes', name: "Yes" },
	{ id: 'no', name: "No" } 
	]}
], valueExpr: 'id', displayExpr: 'name', grouped: true };


</script>

<style lang="scss">
@import '../themes/generated/variables.base.scss';
.video-popup .dx-popup-content {
	overflow: hidden;
}
.survey-forms {
	.header-image {
		width: 100%;
		max-width: 1200px;
	}
	.header-title {
		text-align: center;
		text-transform: uppercase;
		width: 100%; max-width: 1200px;
		border-bottom: 4px double black;
	}
	.header-subtitle {
		text-align: center;
		width: 100%; max-width: 1200px;
		font-size: 20px;
	}
	.application-form {
		max-width: 1200px;
	}
	.simple-item-descriptive {
		padding: 0 20px;
		border-left: 6px solid;
		text-align: justify;
	}
	.simple-item-descriptive p+p {
		margin-top: 0;
	}
	.simple-item-descriptive-title {
		font-weight: bold;
		font-size: 1.2em;
	}
	.simple-item-videobox {
		display: flex;
		justify-content: center;
		color: #efefef;
		margin: 10px;
		font-size: 24px;
		text-align: center;
		background: #ccc;
		background-image: url(../assets/general_survey_vid.th.jpg);
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		border: 2px solid #999;
		text-shadow: 2px 2px 3px #040404;
		cursor: pointer;
		min-height: 150px;
	}
	.submitsurvey-error-body {
		white-space: pre-line;
		border-left: 2px solid red;
		padding-left: 15px;
	}
	.existing-warning {
		text-align: center;
	}
	.group-hint-header {
		font-style: italic;
		padding: 0 !important;
		span {
			vertical-align: middle;
		}
		i {
			vertical-align: middle;
			padding-right: 5px;
		}
	}
	.language-row {
		padding: 10px 20px;
		border: 1px solid $base-border-color;
		border-left: 15px solid $base-border-color;
		margin-bottom: 1em;
		.language-level {
			background: $base-border-color;
			border: 1px solid $base-border-color;
			border-radius: 5px;
			padding: 2px 5px;
			margin-left: 10px;
		}
		.language-remove {
			cursor: pointer;
			float: right;
			margin-top: -4px;
			color: #f44336;
		}
	}
	.list-tiny {
		padding-inline-start: 15px;
	}
}
</style>
